import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Eye, EyeOff, Download, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [mode, setMode]         = useState('login'); // 'login' | 'forgot'
  const [sent, setSent]         = useState(false);
  const [config, setConfig]     = useState(null);

  useEffect(() => {
    api.get('/guest/config').then(d => setConfig(d.settings)).catch(() => {});
  }, []);

  const logoUrl = config?.logo_url
    ? (config.logo_url.startsWith('/uploads/') ? `/api${config.logo_url}` : config.logo_url)
    : null;

  const siteName = config?.site_name || 'GT Computing';
  const btnEnabled = config?.login_button_enabled === 'true';
  const btnLabel   = config?.login_button_label  || 'Download Remote Access';
  const btnUrl     = config?.login_button_url    || '#';

  const handleLogin = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('If that email exists, a reset link has been sent.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}>
      {/* Grid background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.5 }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 400 }}>
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={siteName}
              style={{ maxHeight: 64, maxWidth: 240, objectFit: 'contain', marginBottom: 16 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, background: 'var(--accent)', borderRadius: 12, marginBottom: 16 }}>
              <Zap size={22} color="white" />
            </div>
          )}
          <h1 style={{ fontSize: 22, letterSpacing: '-0.04em', marginBottom: 4 }}>{siteName}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Help Desk Portal</p>
        </div>

        {/* Card */}
        <div className="card card-lg" style={{ boxShadow: 'var(--shadow-lg)' }}>
          {mode === 'login' ? (
            <>
              <h2 style={{ fontSize: 17, marginBottom: 20 }}>Sign in to your account</h2>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
                </div>
                <div className="form-group">
                  <div className="flex justify-between items-center" style={{ marginBottom: 6 }}>
                    <label className="form-label" style={{ marginBottom: 0 }}>Password</label>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 12 }} onClick={() => setMode('forgot')}>Forgot password?</button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input className="form-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowPw(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button className="btn btn-primary w-full" style={{ justifyContent: 'center', marginTop: 4 }} disabled={loading}>
                  {loading ? <><div className="spinner" style={{ width: 15, height: 15, borderWidth: 2 }} /> Signing in…</> : 'Sign in'}
                </button>
              </form>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>

              {/* Guest submit ticket */}
              <button
                className="btn btn-secondary w-full"
                style={{ justifyContent: 'center' }}
                onClick={() => navigate('/submit')}
              >
                Submit a ticket without logging in
              </button>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 17, marginBottom: 8 }}>Reset your password</h2>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📬</div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, marginBottom: 16 }}>Reset link generated. Check your server logs, or ask your admin.</p>
                  <button className="btn btn-secondary" onClick={() => { setMode('login'); setSent(false); }}>Back to sign in</button>
                </div>
              ) : (
                <>
                  <p className="text-secondary text-sm" style={{ marginBottom: 20 }}>Enter your email and we'll log a reset link to the server console.</p>
                  <form onSubmit={handleForgot}>
                    <div className="form-group">
                      <label className="form-label">Email address</label>
                      <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className="btn btn-secondary" onClick={() => setMode('login')}>Back</button>
                      <button className="btn btn-primary flex-1" style={{ justifyContent: 'center' }} disabled={loading}>{loading ? 'Sending…' : 'Send reset link'}</button>
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>

        {/* Optional download button */}
        {btnEnabled && btnUrl && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <a
              href={btnUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary w-full"
              style={{ justifyContent: 'center', display: 'inline-flex', gap: 8 }}
            >
              <Download size={15} />
              {btnLabel}
              <ExternalLink size={12} style={{ opacity: 0.5 }} />
            </a>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 20, color: 'var(--text-muted)', fontSize: 12 }}>
          Need access? Contact your administrator.
        </p>
      </div>
    </div>
  );
}
